(function (global, document) {
  'use strict';

  var API_VERSION = '1.0.0';
  var SCHEMA_VERSION = 1;
  var STORAGE_KEY = 'avontic_device_profile_v1';
  var MAX_DEVICES = 18;
  var MAX_LIST_VALUES = 10;
  var subscribers = [];
  var memoryState = null;
  var storageAvailable = true;

  if (global.AvonticDeviceProfile && global.AvonticDeviceProfile.version === API_VERSION) {
    if (typeof global.AvonticDeviceProfile.reconcile === 'function') {
      global.AvonticDeviceProfile.reconcile({ source: 'compatibility-ready' });
    }
    return;
  }

  var ROUTES = Object.freeze({
    identify: '/pages/my-devices#add-device',
    recommend: '/pages/protection-planner',
    compare: '/pages/compare-products',
    tablet: '/pages/build-your-setup',
    loadout: '/pages/build-your-loadout',
    business: '/pages/business-solutions',
    devices: '/pages/my-devices'
  });

  function safeParse(value, fallback) {
    if (typeof value !== 'string' || !value.trim()) return fallback;
    try {
      var parsed = JSON.parse(value);
      return parsed == null ? fallback : parsed;
    } catch (error) {
      return fallback;
    }
  }

  function safeRead(storage, key) {
    try {
      return storage ? storage.getItem(key) : null;
    } catch (error) {
      storageAvailable = false;
      return null;
    }
  }

  function safeWrite(key, value) {
    memoryState = value;
    try {
      global.localStorage.setItem(key, JSON.stringify(value));
      storageAvailable = true;
      return true;
    } catch (error) {
      storageAvailable = false;
      return false;
    }
  }

  function cleanText(value, limit) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, limit || 120);
  }

  function cleanList(value) {
    var source = Array.isArray(value) ? value : value ? [value] : [];
    return source.map(function (item) { return cleanText(item, 50); })
      .filter(Boolean)
      .filter(function (item, index, list) { return list.indexOf(item) === index; })
      .slice(0, MAX_LIST_VALUES);
  }

  function copy(value) {
    if (value == null) return value;
    return safeParse(JSON.stringify(value), value);
  }

  function blankState() {
    return {
      schemaVersion: SCHEMA_VERSION,
      active: null,
      devices: [],
      preferences: {
        useCases: [],
        priorities: [],
        finish: '',
        protection: '',
        category: ''
      },
      migration: { completed: false, sources: [] },
      source: '',
      updatedAt: 0
    };
  }

  function deviceInputLabel(input) {
    if (typeof input === 'string') return input;
    if (!input || typeof input !== 'object') return '';
    return input.label || input.model || input.device || input.name || '';
  }

  function normalizeDevice(input) {
    var label = cleanText(deviceInputLabel(input), 120);
    if (!label) return null;

    var resolved = null;
    try {
      if (global.AvonticCompatibility && typeof global.AvonticCompatibility.resolveDevice === 'function') {
        resolved = global.AvonticCompatibility.resolveDevice(label);
      }
    } catch (error) {
      resolved = null;
    }

    var source = resolved && typeof resolved === 'object' ? resolved : (typeof input === 'object' ? input : {});
    var normalized = {
      key: cleanText(source.key || source.id || label.toLowerCase().replace(/[^a-z0-9]+/g, '-'), 120),
      label: cleanText(source.label || label, 120),
      family: cleanText(source.family || source.brand || '', 70),
      kind: cleanText(source.kind || source.category || '', 40),
      model: cleanText(source.model || label, 90),
      edition: cleanText(source.edition || '', 40),
      size: cleanText(source.size || '', 24),
      generation: cleanText(source.generation || '', 24),
      year: cleanText(source.year || '', 8),
      chip: cleanText(source.chip || '', 24),
      fitGroup: cleanText(source.fitGroup || '', 100),
      code: cleanText(source.code || '', 24),
      connector: cleanText(source.connector || '', 12)
    };

    if (!normalized.key || !normalized.label) return null;
    return normalized;
  }

  function uniqueDevices(devices) {
    var byKey = {};
    return devices.map(normalizeDevice).filter(Boolean).filter(function (device) {
      var key = device.key || device.label.toLowerCase();
      if (byKey[key]) return false;
      byKey[key] = true;
      return true;
    }).slice(0, MAX_DEVICES);
  }

  function sanitizePreferences(value) {
    var input = value && typeof value === 'object' ? value : {};
    return {
      useCases: cleanList(input.useCases || input.uses),
      priorities: cleanList(input.priorities),
      finish: cleanText(input.finish, 40),
      protection: cleanText(input.protection || input.preference, 50),
      category: cleanText(input.category, 40)
    };
  }

  function sanitizeState(value) {
    var input = value && typeof value === 'object' ? value : {};
    var devices = uniqueDevices(Array.isArray(input.devices) ? input.devices : []);
    var active = normalizeDevice(input.active);
    if (active && !devices.some(function (device) { return device.key === active.key; })) devices.unshift(active);
    devices = uniqueDevices(devices);

    return {
      schemaVersion: SCHEMA_VERSION,
      active: active,
      devices: devices,
      preferences: sanitizePreferences(input.preferences),
      migration: {
        completed: Boolean(input.migration && input.migration.completed),
        sources: cleanList(input.migration && input.migration.sources)
      },
      source: cleanText(input.source, 40),
      updatedAt: Number(input.updatedAt) || 0
    };
  }

  function readCurrent() {
    var stored = safeParse(safeRead(global.localStorage, STORAGE_KEY), null);
    if (stored && typeof stored === 'object') {
      memoryState = sanitizeState(stored);
      return memoryState;
    }
    return memoryState ? sanitizeState(memoryState) : blankState();
  }

  function mergePreferences(primary, secondary) {
    var first = sanitizePreferences(primary);
    var second = sanitizePreferences(secondary);
    return {
      useCases: cleanList(first.useCases.concat(second.useCases)),
      priorities: cleanList(first.priorities.concat(second.priorities)),
      finish: first.finish || second.finish,
      protection: first.protection || second.protection,
      category: first.category || second.category
    };
  }

  function legacySnapshot() {
    var devices = [];
    var active = null;
    var preferences = {};
    var sources = [];
    var queryDevice = '';

    try {
      var params = new URLSearchParams(global.location.search);
      queryDevice = params.get('verified_device') || params.get('verifiedDevice') || params.get('device') || '';
    } catch (error) {
      queryDevice = '';
    }
    if (queryDevice && queryDevice.length < 140) {
      active = normalizeDevice(queryDevice);
      if (active) {
        devices.push(active);
        sources.push('url');
      }
    }

    var handoff = safeParse(safeRead(global.sessionStorage, 'avontic_device_handoff_v1'), null);
    if (handoff && handoff.device) {
      var handoffDevice = normalizeDevice(handoff.device);
      if (handoffDevice) {
        devices.push(handoffDevice);
        if (!active) active = handoffDevice;
        sources.push('my-devices-handoff');
      }
    }
    if (handoff && Array.isArray(handoff.allDevices)) {
      devices = devices.concat(handoff.allDevices);
      sources.push('my-devices-handoff-list');
    }

    var garage = safeParse(safeRead(global.localStorage, 'avontic_device_garage_v1'), null);
    if (garage && Array.isArray(garage.devices)) {
      devices = devices.concat(garage.devices);
      var garageActive = garage.devices.find(function (device) { return String(device.id) === String(garage.activeId); });
      if (!active && garageActive) active = normalizeDevice(garageActive);
      preferences.protection = cleanText(garage.preference, 50);
      sources.push('my-devices');
    }

    var comparison = safeParse(safeRead(global.localStorage, 'avontic-comparison'), null);
    if (!active && comparison && comparison.device) {
      active = normalizeDevice(comparison.device);
      if (active) devices.unshift(active);
      sources.push('compare');
    }

    var oldDevice = safeRead(global.localStorage, 'avontic-device');
    if (!active && oldDevice) {
      active = normalizeDevice(oldDevice);
      if (active) devices.unshift(active);
      sources.push('legacy-device');
    }

    var planner = safeParse(safeRead(global.sessionStorage, 'avpp_planner_state_v3'), null);
    if (planner && planner.answers) {
      if (!active && planner.answers.device) {
        active = normalizeDevice(planner.answers.device);
        if (active) devices.unshift(active);
      }
      preferences = mergePreferences(preferences, {
        useCases: planner.answers.useCases,
        priorities: planner.answers.priorities,
        category: planner.answers.category
      });
      sources.push('protection-planner');
    }

    var setup = safeParse(safeRead(global.localStorage, 'avontic-complete-setup-studio-v2'), null)
      || safeParse(safeRead(global.localStorage, 'avontic-complete-setup-studio-v1'), null);
    if (setup) {
      preferences = mergePreferences(preferences, {
        useCases: setup.environment,
        priorities: setup.priorities,
        protection: setup.caseChoice,
        category: setup.family
      });
      sources.push('complete-setup');
    }

    var loadout = safeParse(safeRead(global.localStorage, 'avontic-loadout-studio-v2'), null)
      || safeParse(safeRead(global.localStorage, 'avontic-loadout-studio-v1'), null);
    if (loadout) {
      preferences = mergePreferences(preferences, {
        useCases: loadout.lifestyle,
        finish: loadout.finish
      });
      sources.push('loadout');
    }

    return {
      active: active,
      devices: uniqueDevices(devices),
      preferences: sanitizePreferences(preferences),
      sources: cleanList(sources)
    };
  }

  function emit(name, state, metadata) {
    var detail = {
      version: API_VERSION,
      state: copy(state),
      source: cleanText(metadata && metadata.source || state.source, 40),
      reason: cleanText(metadata && metadata.reason, 60),
      persisted: storageAvailable
    };
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (error) {}
    subscribers.slice().forEach(function (subscriber) {
      try { subscriber(copy(state), detail); } catch (error) {}
    });
  }

  function track(action, state, destination) {
    if (!global.AvonticAnalytics || typeof global.AvonticAnalytics.track !== 'function') return;
    try {
      global.AvonticAnalytics.track('guided_device_profile', {
        component: 'guided_shopping_os',
        action: cleanText(action, 40),
        compatibility_key: state.active ? state.active.key : '',
        compatibility_state: state.active ? 'known' : 'unknown',
        destination: cleanText(destination, 80),
        result_count: state.devices.length
      });
    } catch (error) {}
  }

  function commit(next, metadata) {
    var state = sanitizeState(next);
    state.updatedAt = Date.now();
    state.source = cleanText(metadata && metadata.source || 'api', 40);
    safeWrite(STORAGE_KEY, state);
    emit('avontic:device-profile:change', state, metadata || {});
    decorateLinks(state);
    track(metadata && metadata.action || 'updated', state, metadata && metadata.destination);
    return copy(state);
  }

  function migrate() {
    var current = readCurrent();
    if (current.migration.completed) return current;
    var legacy = legacySnapshot();
    var merged = sanitizeState({
      active: current.active || legacy.active,
      devices: (current.devices || []).concat(legacy.devices || []),
      preferences: mergePreferences(current.preferences, legacy.preferences),
      migration: { completed: true, sources: legacy.sources },
      source: 'migration',
      updatedAt: Date.now()
    });
    safeWrite(STORAGE_KEY, merged);
    return merged;
  }

  function setActive(input, options) {
    var device = normalizeDevice(input);
    if (!device) return copy(readCurrent());
    var current = readCurrent();
    current.active = device;
    current.devices = uniqueDevices([device].concat(current.devices || []));
    return commit(current, {
      source: options && options.source || 'api',
      reason: options && options.reason || 'active-device',
      action: 'device_selected',
      destination: options && options.destination
    });
  }

  function setDevices(inputs, options) {
    var devices = uniqueDevices(Array.isArray(inputs) ? inputs : [inputs]);
    if (!devices.length) return copy(readCurrent());
    var current = readCurrent();
    current.devices = uniqueDevices(devices.concat(current.devices || []));
    if (!current.active || options && options.activateFirst) current.active = devices[0];
    return commit(current, {
      source: options && options.source || 'api',
      reason: 'device-list',
      action: 'devices_updated',
      destination: options && options.destination
    });
  }

  function setPreferences(preferences, options) {
    var current = readCurrent();
    current.preferences = mergePreferences(sanitizePreferences(preferences), current.preferences);
    return commit(current, {
      source: options && options.source || 'api',
      reason: 'preferences',
      action: 'preferences_updated'
    });
  }

  function reconcile(options) {
    var current = readCurrent();
    var before = JSON.stringify(current);
    current.active = normalizeDevice(current.active);
    current.devices = uniqueDevices((current.active ? [current.active] : []).concat(current.devices || []));
    var after = JSON.stringify(current);
    if (before === after) {
      decorateLinks(current);
      return copy(current);
    }
    return commit(current, {
      source: options && options.source || 'api',
      reason: 'reconciled',
      action: 'profile_reconciled'
    });
  }

  function clear(options) {
    var next = blankState();
    next.migration.completed = true;
    return commit(next, {
      source: options && options.source || 'api',
      reason: 'cleared',
      action: 'profile_cleared'
    });
  }

  function route(destination, params) {
    var path = ROUTES[destination] || destination || '/';
    var url;
    try {
      url = new URL(path, global.location.origin);
    } catch (error) {
      return path;
    }
    var state = readCurrent();
    var active = state.active;
    if (active && !url.searchParams.has('device')) {
      if (url.pathname.indexOf('/build-your-setup') !== -1 || url.pathname.indexOf('/build-your-loadout') !== -1) {
        url.searchParams.set('device', active.key || active.label);
      } else {
        url.searchParams.set('device', active.label);
      }
    }
    Object.keys(params || {}).forEach(function (key) {
      var value = cleanText(params[key], 160);
      if (value) url.searchParams.set(key, value);
    });
    return url.pathname + url.search + url.hash;
  }

  function decorateLinks(state) {
    var active = state && state.active;
    Array.prototype.forEach.call(document.querySelectorAll('[data-avontic-profile-link]'), function (link) {
      var destination = link.getAttribute('data-avontic-profile-link') || link.getAttribute('href') || '/';
      link.setAttribute('href', route(destination));
      if (active) link.setAttribute('data-avontic-device-ready', 'true');
      else link.removeAttribute('data-avontic-device-ready');
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-avontic-profile-label]'), function (target) {
      target.textContent = active ? 'Using ' + active.label : 'Start anywhere. Your device will carry across.';
      target.setAttribute('data-has-device', active ? 'true' : 'false');
    });
  }

  function captureEvents() {
    global.addEventListener('avontic:compare', function (event) {
      var detail = event && event.detail || {};
      if (detail.event === 'compare_device_confirmed' && detail.device) {
        setActive(detail.device, { source: 'compare', reason: 'confirmed' });
      }
    });

    document.addEventListener('avontic:analytics', function (event) {
      var detail = event && event.detail || {};
      if ((detail.name === 'device_garage_device_saved' || detail.name === 'device_garage_device_updated') && detail.model) {
        setActive({ label: detail.model, model: detail.model, category: detail.category }, { source: 'my-devices', reason: detail.name });
      }
      if (detail.event === 'exact_device_confirmed' || detail.name === 'exact_device_confirmed') {
        var planner = safeParse(safeRead(global.sessionStorage, 'avpp_planner_state_v3'), null);
        if (planner && planner.answers && planner.answers.device) {
          setActive(planner.answers.device, { source: 'protection-planner', reason: 'confirmed' });
          setPreferences({
            useCases: planner.answers.useCases,
            priorities: planner.answers.priorities,
            category: planner.answers.category
          }, { source: 'protection-planner' });
        }
      }
    });

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.matches) return;
      if (target.matches('[data-avss-device]')) {
        var setupLabel = target.options && target.selectedIndex >= 0 ? target.options[target.selectedIndex].text : '';
        if (target.value && setupLabel) setActive({ key: target.value, label: setupLabel }, { source: 'complete-setup', reason: 'device-select' });
      }
      if (target.matches('[data-avls-device]')) {
        var loadoutLabel = target.options && target.selectedIndex >= 0 ? target.options[target.selectedIndex].text : '';
        if (target.value && loadoutLabel) setActive({ key: target.value, label: loadoutLabel, category: target.getAttribute('data-avls-device') }, { source: 'loadout', reason: 'device-select' });
      }
      if (target.matches('[data-avpp-device]') && target.value) {
        setActive(target.value, { source: 'protection-planner', reason: 'device-input' });
      }
    }, true);

    global.addEventListener('storage', function (event) {
      if (event.key !== STORAGE_KEY) return;
      var next = sanitizeState(safeParse(event.newValue, blankState()));
      memoryState = next;
      emit('avontic:device-profile:change', next, { source: 'another-tab', reason: 'storage' });
      decorateLinks(next);
    });

    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest ? event.target.closest('[data-avontic-profile-link]') : null;
      if (!link) return;
      var state = readCurrent();
      track('route_selected', state, link.getAttribute('data-avontic-profile-link') || link.getAttribute('href'));
    });
  }

  function subscribe(callback) {
    if (typeof callback !== 'function') return function () {};
    subscribers.push(callback);
    return function () {
      subscribers = subscribers.filter(function (item) { return item !== callback; });
    };
  }

  var initial = migrate();
  var api = {
    version: API_VERSION,
    schemaVersion: SCHEMA_VERSION,
    storageKey: STORAGE_KEY,
    routes: ROUTES,
    get: function () { return copy(readCurrent()); },
    getActive: function () { return copy(readCurrent().active); },
    setActive: setActive,
    setDevice: setActive,
    setDevices: setDevices,
    setPreferences: setPreferences,
    reconcile: reconcile,
    clear: clear,
    route: route,
    subscribe: subscribe,
    normalize: normalizeDevice
  };

  global.AvonticDeviceProfile = Object.freeze(api);
  captureEvents();

  function ready() {
    var state = readCurrent();
    decorateLinks(state);
    emit('avontic:device-profile:ready', state, { source: initial.source || 'bootstrap', reason: 'ready' });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
})(window, document);

(function () {
  'use strict';

  var base = window.AvonticCompatibility;
  if (!base || typeof base.resolveDevice !== 'function' || typeof base.matchVariant !== 'function') return;

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, ' and ')
      .replace(/[™®©]/g, '')
      .replace(/(\d(?:\.\d+)?)\s*[\"”]/g, '$1 inch')
      .replace(/\b(fold|flip)(\d+)\b/g, '$1 $2')
      .replace(/[^a-z0-9.]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function titleCase(value) {
    return String(value || '').replace(/\b\w/g, function (letter) { return letter.toUpperCase(); })
      .replace(/Ipad/g, 'iPad')
      .replace(/Iphone/g, 'iPhone');
  }

  function unique(values) {
    return values.filter(function (value, index) { return value && values.indexOf(value) === index; });
  }

  function signalList(profile) {
    return [
      { label: 'Family', value: profile.family },
      profile.size ? { label: 'Physical fit', value: /mm$/.test(profile.size) ? profile.size : profile.size + '-inch' } : null,
      profile.generation ? { label: 'Generation', value: profile.generation } : null,
      profile.chip ? { label: 'Chip', value: String(profile.chip).toUpperCase() } : null,
      profile.year ? { label: 'Model year', value: profile.year } : null,
      profile.edition ? { label: 'Edition', value: titleCase(profile.edition) } : null
    ].filter(Boolean);
  }

  function phoneProfile(raw, kind, family, model, edition, prefix) {
    var cleanEdition = String(edition || 'base').replace(/\s+/g, ' ');
    var modelSignals = prefix === 's'
      ? ['s' + model, 's ' + model]
      : [String(model), prefix + ' ' + model, prefix + model];
    var profile = {
      key: family.toLowerCase().replace(/\s+/g, '-') + '-' + model + '-' + cleanEdition.replace(/\s+/g, '-'),
      label: raw,
      family: family,
      kind: kind,
      model: String(model),
      edition: cleanEdition,
      groups: [[prefix]].concat([modelSignals]).concat(cleanEdition !== 'base' ? [[cleanEdition]] : []),
      variantGroups: [modelSignals].concat(cleanEdition !== 'base' ? [[cleanEdition]] : []),
      signals: []
    };
    profile.signals = signalList(profile);
    return profile;
  }

  function collect(text, pattern) {
    var values = [];
    var match;
    while ((match = pattern.exec(text))) values.push(match[1]);
    return unique(values);
  }

  function iPadDetails(value) {
    var source = String(value || '').toLowerCase();
    var text = normalize(source);
    if (!/\bipad\b/.test(text)) return null;
    var family = (text.match(/ipad\s+(air|pro|mini)\b/) || [])[1] || 'base';
    var size = (text.match(/\bipad(?:\s+(?:air|pro|mini))?\s+(7\.9|8\.3|9\.7|10\.2|10\.5|10\.9|11|12\.9|13)(?:\s*(?:inch|in))?\b/) || [])[1] || '';
    var generations = unique(collect(source, /\b(\d+)(?:st|nd|rd|th)\s*(?:generation|gen)?\b/g)
      .concat(collect(source, /\b(?:generation|gen)\s*(\d+)\b/g)));
    var years = collect(source, /\b(20\d{2})\b/g);
    var chips = unique(collect(source, /\b(m\d+)\b/g).concat(collect(source, /\b(a(?:14|15|16|17|18|19)(?:\s+pro)?)\b/g)));

    var range;
    var yearRange = /\b(20\d{2})\s*(?:-|–|—|to)\s*(20\d{2})\b/g;
    while ((range = yearRange.exec(source))) {
      for (var year = Number(range[1]); year <= Number(range[2]) && year <= Number(range[1]) + 8; year += 1) years.push(String(year));
    }
    years = unique(years);

    return { text: text, family: family, size: size, generations: generations, years: years, chips: chips };
  }

  function iPadPro11Group(details) {
    if (!details || details.family !== 'pro' || details.size !== '11') return '';
    if (details.chips.some(function (chip) { return chip === 'm4' || chip === 'm5'; }) || details.years.some(function (year) { return year === '2024' || year === '2025'; })) return 'ipad-pro-11-m4-m5';
    if (details.generations.some(function (generation) { return generation === '1' || generation === '2'; }) || details.years.some(function (year) { return year === '2018' || year === '2020'; })) return 'ipad-pro-11-gen1-2';
    if (details.generations.some(function (generation) { return generation === '3' || generation === '4'; }) || details.years.some(function (year) { return year === '2021' || year === '2022'; })) return 'ipad-pro-11-gen3-4';
    return '';
  }

  function preciseIpadProfile(raw) {
    var details = iPadDetails(raw);
    var fitGroup = iPadPro11Group(details);
    if (!fitGroup) return null;
    var generation = details.generations[0] || '';
    var year = details.years[0] || '';
    var chip = details.chips[0] || '';
    var labelByGroup = {
      'ipad-pro-11-gen1-2': 'iPad Pro 11-inch (1st/2nd gen · 2018–2020)',
      'ipad-pro-11-gen3-4': 'iPad Pro 11-inch (3rd/4th gen · 2021–2022)',
      'ipad-pro-11-m4-m5': 'iPad Pro 11-inch (M4/M5 · 2024–2025)'
    };
    var profile = {
      key: fitGroup,
      label: labelByGroup[fitGroup],
      family: 'Apple iPad Pro',
      kind: 'ipad',
      model: 'pro',
      size: '11',
      generation: generation,
      year: year,
      chip: chip,
      fitGroup: fitGroup,
      groups: [['ipad'], ['pro'], ['11']].concat(generation ? [[generation]] : []).concat(year ? [[year]] : []).concat(chip ? [[chip]] : []),
      variantGroups: [['pro'], ['11']].concat(generation ? [[generation]] : []).concat(year ? [[year]] : []).concat(chip ? [[chip]] : []),
      signals: []
    };
    profile.signals = signalList(profile);
    return profile;
  }

  function enhancedResolve(raw) {
    if (/\b(?:apple\s*watch|iwatch)\s*(?:series\s*12|ultra\s*4)\b|^(?:series\s*12|ultra\s*4)\b/i.test(String(raw || ''))) return window.AvonticWatchQualifiedFit ? window.AvonticWatchQualifiedFit.resolveDevice(raw) : { key: String(raw).toLowerCase(), label: String(raw), family: 'Apple Watch', kind: 'apple-watch', groups: [['apple watch']], variantGroups: [], ambiguity: "New-model accessory fit confirmation is pending. Explore the Apple Watch launch guide or select a currently supported model.", signals: [] };
    var text = normalize(raw);

    if (/\biphone\s*duo\b/.test(text)) {
      return phoneProfile('iPhone Duo', 'iphone', 'Apple iPhone', 'duo', 'base', 'iphone');
    }

    if (/\biphone\s*17\s*air\b/.test(text)) {
      var iphoneAir = base.resolveDevice(raw);
      iphoneAir.key = 'iphone-17-air';
      iphoneAir.label = 'iPhone 17 Air';
      iphoneAir.model = '17';
      iphoneAir.edition = 'air';
      iphoneAir.signals = signalList(iphoneAir);
      return iphoneAir;
    }

    if (!/\bwatch\b/.test(text)) {
      var galaxyPhone = text.match(/(?:samsung\s*)?(?:galaxy\s*)?s\s*(\d+)\s*(ultra|plus|\+|edge|fe)?\b/);
      if (galaxyPhone && (/\b(?:samsung|galaxy)\b/.test(text) || /^s\s*\d+/.test(text))) {
        var galaxyEdition = galaxyPhone[2] === '+' ? 'plus' : galaxyPhone[2] || 'base';
        return phoneProfile('Samsung Galaxy S' + galaxyPhone[1] + (galaxyEdition === 'base' ? '' : ' ' + titleCase(galaxyEdition)), 'galaxy-phone', 'Samsung Galaxy S', galaxyPhone[1], galaxyEdition, 'galaxy');
      }
    }

    if (!/\bwatch\b/.test(text)) {
      var pixelPhone = text.match(/(?:google\s*)?pixel\s*(\d+[a-z]?)\s*(pro\s*fold|pro\s*xl|pro|xl|fold|a)?\b/);
      if (pixelPhone) {
        var pixelEdition = (pixelPhone[2] || 'base').replace(/\s+/g, ' ');
        return phoneProfile('Google Pixel ' + pixelPhone[1] + (pixelEdition === 'base' ? '' : ' ' + titleCase(pixelEdition)), 'pixel-phone', 'Google Pixel', pixelPhone[1], pixelEdition, 'pixel');
      }
    }

    var preciseIpad = preciseIpadProfile(raw);
    if (preciseIpad) return preciseIpad;

    return base.resolveDevice(raw);
  }

  function variantSources(product, variant) {
    var optionSources = unique([variant.option1, variant.option2, variant.option3, variant.title]).filter(function (value) {
      return /iphone|ipad|apple\s*watch|iwatch|galaxy|samsung|pixel|surface|fitbit|garmin|razr|oneplus|\ba\d{4}\b/i.test(String(value || ''));
    });
    if (optionSources.length) return optionSources;
    return [product.title, (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',')).join(' ')];
  }

  function siblingEditionMatch(sourceProfile, targetProfile) {
    return sourceProfile && sourceProfile.kind === targetProfile.kind &&
      String(sourceProfile.model || '') === String(targetProfile.model || '') &&
      String(sourceProfile.edition || 'base') === String(targetProfile.edition || 'base');
  }

  function strictPhoneMatch(product, variant, profile) {
    return variantSources(product, variant).some(function (source) {
      var sourceProfile = enhancedResolve(source);
      return siblingEditionMatch(sourceProfile, profile);
    });
  }

  function strictIpadMatch(product, variant, profile) {
    var targetDetails = iPadDetails(profile.label + ' ' + (profile.generation || '') + ' ' + (profile.year || '') + ' ' + (profile.chip || ''));
    var targetGroup = profile.fitGroup || iPadPro11Group(targetDetails);
    return variantSources(product, variant).some(function (source) {
      var sourceDetails = iPadDetails(source);
      if (!sourceDetails) return false;
      var sourceGroup = iPadPro11Group(sourceDetails);
      if (targetGroup && sourceGroup) return targetGroup === sourceGroup;
      return false;
    });
  }

  function enhancedMatchVariant(product, variant, profile) {
    if (profile && /\b(?:apple\s*watch|iwatch)\s*(?:series\s*12|ultra\s*4)\b|^(?:series\s*12|ultra\s*4)\b/i.test(String(profile.label || ''))) return window.AvonticWatchQualifiedFit ? window.AvonticWatchQualifiedFit.matchVariant(product, variant, profile) : { matched: false, confidence: 'none', reason: 'Watch fit data is unavailable. Choose a currently supported option.', source: variant && variant.title || '', signals: [] };
    if (!variant || variant.available === false || !profile || !profile.groups || !profile.groups.length || profile.ambiguity) {
      return { matched: false, confidence: 'none', reason: 'The device identity is incomplete or the variant is unavailable.', source: variant && variant.title || '', signals: [] };
    }

    var matched;
    if (profile.kind === 'galaxy-phone' || profile.kind === 'pixel-phone' || profile.kind === 'iphone' || profile.kind === 'galaxy-fold' || profile.kind === 'galaxy-flip') {
      matched = strictPhoneMatch(product, variant, profile);
    } else if (profile.kind === 'ipad' && profile.fitGroup && /^ipad-pro-11-(?:gen1-2|gen3-4|m4-m5)$/.test(profile.fitGroup)) {
      matched = strictIpadMatch(product, variant, profile);
    } else {
      return base.matchVariant(product, variant, profile);
    }

    return {
      matched: matched,
      confidence: matched ? 'exact' : 'none',
      reason: matched ? 'The purchasable option resolves to the same exact device family, model and edition.' : 'A neighboring device model, edition, size or generation was excluded.',
      source: variant.title || product.title || '',
      signals: matched ? (profile.signals || []).map(function (signal) { return signal.label + ': ' + signal.value; }) : []
    };
  }

  function enhancedMatchingVariants(product, profile) {
    return profile ? (product.variants || []).filter(function (variant) {
      return enhancedMatchVariant(product, variant, profile).matched;
    }) : [];
  }

  window.AvonticCompatibility = {
    normalizeModel: base.normalizeModel,
    resolveDevice: enhancedResolve,
    matchVariant: enhancedMatchVariant,
    matchingVariants: enhancedMatchingVariants
  };
})();


